interface TeamVanishedOptions {
  teamId: string
  activeTeamId: string | null
  /** Renews the access token so its groups claim drops a deleted team. */
  refreshToken: () => Promise<void>
  /** Asks /me again and resolves with the fresh team list. */
  refetchTeams: () => Promise<readonly { id: string }[]>
  switchTeam: (teamId: string) => void
}

/**
 * A running delete left the owner's list. That happens when the team is
 * gone (its group is the delete's last step, and the list drops a team
 * whose group is gone, so an owner's finished delete always leaves the
 * list rather than turning done), but also when ownership moved
 * mid-delete or a token refresh dropped the group while the team still
 * exists. So the token is renewed first (the token still lists the team
 * until it is renewed, and both the team list and the api read teams
 * from it; a refresh that fails is logged and the flow goes on, since the
 * next renewal brings the groups claim up to date) and /me asked again,
 * and only a team missing from the fresh list counts as deleted: `gone`,
 * or `switched` when it was the active team and the page moves to
 * another. A team still listed is `kept`: nothing is said about it.
 */
export async function afterTeamVanished({
  teamId,
  activeTeamId,
  refreshToken,
  refetchTeams,
  switchTeam,
}: TeamVanishedOptions): Promise<'gone' | 'switched' | 'kept'> {
  try {
    await refreshToken()
  } catch (error: unknown) {
    console.warn('Token refresh after a team delete left the list failed:', error)
  }
  const fresh = await refetchTeams()
  if (fresh.some((t) => t.id === teamId)) return 'kept'
  if (teamId === activeTeamId) {
    const next = fresh.find((t) => t.id !== teamId)
    if (next) {
      switchTeam(next.id)
      return 'switched'
    }
  }
  return 'gone'
}
